<script setup>
import { onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import FlashcardDeck from "@/components/FlashcardDeck.vue";

const router = useRouter();

let previousTheme = null;
let previousBodyPadding = "";
let previousMobileHeaderDisplay = "";

const demoCards = [
  {
    id: "demo-1",
    language: "es",
    language_sentence: "Yo quiero tomar café por la mañana.",
    language_word: "tomar",
    language_sentence_mappings: {
      en: "I want to drink coffee in the morning.",
    },
    language_word_mappings: { en: "to drink" },
    language_word_synonyms: ["beber"],
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Spanish%20Demo%20Sentence.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Spanish%20Demo%20Word(1).mp3",
    state: 1,
    part_of_speech: "verb",
    tense: "infinitive",
    person: "1st",
  },
  {
    id: "demo-2",
    language: "fr",
    language_sentence: "Elle lit un livre intéressant.",
    language_word: "livre",
    language_sentence_mappings: { en: "She is reading an interesting book." },
    language_word_mappings: { en: "book" },
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/French%20Demo%20Sentence.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/French%20Demo%20Word.mp3",
    state: 0,
    part_of_speech: "noun",
    gender: "masculine",
    number: "singular",
  },
  {
    id: "demo-3",
    language: "zh",
    language_sentence: "请问，洗手间在哪里？",
    language_word: "在",
    pinyin: "zài",
    language_sentence_mappings: { en: "Excuse me, where is the restroom?" },
    language_word_mappings: { en: "at; in; to be at" },
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Mandarin%20Sentence%20Demo.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Mandarin%20Demo%20Word.mp3",
    state: 2,
    part_of_speech: "preposition",
  },
];

const getStarted = () => {
  router.push({
    path: "/login",
    query: { mode: "signup", redirect: "/onboarding" },
  });
};

onMounted(() => {
  const root = document.documentElement;
  const prev = root.getAttribute("data-theme");
  previousTheme = prev;
  root.setAttribute("data-theme", "light");

  // Landing-only: remove any body padding and remember previous value
  previousBodyPadding = document.body.style.padding || "";
  document.body.style.padding = "0";

  // Hide global mobile Menu toggle while on landing
  try {
    const mh = document.querySelector(".mobile-header");
    if (mh) {
      previousMobileHeaderDisplay = mh.style.display || "";
      mh.style.display = "none";
    }
  } catch (_e) {
    /* no-op */
  }
});

onBeforeUnmount(() => {
  const root = document.documentElement;
  if (previousTheme) {
    root.setAttribute("data-theme", previousTheme);
  } else {
    root.removeAttribute("data-theme");
  }
  // Restore prior body padding
  document.body.style.padding = previousBodyPadding;

  // Restore mobile Menu toggle visibility
  try {
    const mh = document.querySelector(".mobile-header");
    if (mh) {
      mh.style.display = previousMobileHeaderDisplay;
    }
  } catch (_e) {
    /* no-op */
  }
});
</script>

<template>
  <div class="landing">
    <!-- Header -->
    <header class="site-header">
      <div class="container header-inner">
        <div class="brand" @click="router.push('/')">
          <img
            class="brand-logo expanded"
            src="@/assets/Logo Expanded.svg"
            alt="Lexio logo"
          />
          <img
            class="brand-logo symbol"
            src="@/assets/Logo Symbol.svg"
            alt="Lexio logo symbol"
          />
        </div>
        <nav class="nav-cta">
          <router-link class="btn btn-ghost" to="/white-paper"
            >White paper</router-link
          >
          <router-link class="btn btn-ghost" to="/login">Log in</router-link>
          <button class="btn btn-accent" @click="getStarted">
            Get started
          </button>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-bg" aria-hidden="true">
        <div class="gridlines"></div>
      </div>
      <div class="container hero-inner">
        <div class="hero-copy">
          <h1>
            Learn a language by mastering
            <span class="underline">the essentials</span>.
          </h1>
          <p class="subcopy">
            Lexical focuses on what actually compounds: the 5,000 most common
            words, spaced at the right time, always in context — a pragmatic,
            research-backed approach inspired by immersion.
          </p>
          <div class="hero-actions">
            <button class="btn btn-accent btn-lg" @click="getStarted">
              Start free
            </button>
            <router-link class="btn btn-ghost btn-lg" to="/subscriptions">
              See pricing
            </router-link>
          </div>
          <div class="trust">
            <span class="chip">No credit card required</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Principles -->
    <section class="principles">
      <div class="container">
        <h2 class="section-title">Principles that actually work</h2>
        <div class="grid">
          <article class="card">
            <div class="icon">🧠</div>
            <h3>Spaced, not crammed</h3>
            <p>
              Space Repetition Algorithm brings items back just before you
              forget — turning effort into durable memory without burnout.
            </p>
          </article>
          <article class="card">
            <div class="icon">🧩</div>
            <h3>Context first</h3>
            <p>
              Words are introduced in sentences so meaning is inferred the same
              way you learned your first language.
            </p>
          </article>
          <article class="card">
            <div class="icon">🔊</div>
            <h3>Sound matters</h3>
            <p>
              Every card includes audio for the word and sentence to build
              native-like pronunciation from day one.
            </p>
          </article>
          <article class="card">
            <div class="icon">🎯</div>
            <h3>Focus on the 5,000</h3>
            <p>
              The most frequent words unlock most conversations. We prioritize
              them to maximize every minute you study.
            </p>
          </article>
          <article class="card">
            <div class="icon">📉</div>
            <h3>Clarity over clutter</h3>
            <p>
              No gamified distractions. Just clean design, clear goals, and
              steady compounding progress.
            </p>
          </article>
          <article class="card">
            <div class="icon">⚡</div>
            <h3>Designed for momentum</h3>
            <p>
              Simple daily targets and quick sessions keep you consistent —
              where fluency is forged.
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- Live Preview -->
    <section class="preview">
      <div class="container">
        <div class="preview-head">
          <h2 class="section-title">See it in action</h2>
          <p class="lead">
            A lightweight, focused deck experience that emulates immersion. Read
            the white paper to learn more about our learning philosphy.
          </p>
        </div>
        <div class="preview-grid">
          <FlashcardDeck
            :preloaded-cards="demoCards"
            :auto-fetch="false"
            :limit="3"
            :bare="true"
          />
          <div class="notes panel">
            <h3>Why this works</h3>
            <ul>
              <li>Spaced repetition prevents the classic learn–forget loop.</li>
              <li>Context builds intuition faster than rules.</li>
              <li>Audio locks in pronunciation and listening comprehension.</li>
            </ul>
            <div class="mini-cta" style="margin-top: 10px">
              <router-link class="btn btn-ghost" to="/white-paper">
                Read the white paper
              </router-link>
              <button class="btn btn-accent" @click="getStarted">
                Get started
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing CTA -->
    <section class="pricing">
      <div class="container">
        <div class="pricing-card">
          <div class="pricing-copy">
            <h2>Fluency for the price of a cup of coffee</h2>
            <p>
              Simple, transparent pricing to support real learning — not ad
              targeting.
            </p>
          </div>
          <div class="plan">
            <div class="price">
              <span class="amount">$2.99</span>
              <span class="term">/ month</span>
            </div>
            <ul class="features">
              <li>All languages</li>
              <li>Unlimited reviews</li>
              <li>Progress analytics</li>
              <li>Pronunciation audio</li>
            </ul>
            <div class="plan-actions">
              <router-link class="btn btn-ghost btn-lg" to="/subscriptions">
                See details
              </router-link>
              <button class="btn btn-accent btn-lg" @click="getStarted">
                Start free
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="site-footer">
      <div class="container footer-inner">
        <div class="brand-inline">
          <img
            class="brand-logo small"
            src="@/assets/Logo Expanded.svg"
            alt="Lexio logo"
          />
        </div>
        <p class="fine">
          © {{ new Date().getFullYear() }} Lexical ·
          <router-link to="/white-paper">White paper</router-link> ·
          <router-link to="/terms">Terms & Privacy</router-link>
        </p>
      </div>
    </footer>
  </div>
</template>

<style scoped>
/* Layout primitives */
.container {
  width: min(1100px, calc(100% - 32px));
  margin-inline: auto;
}

.section-title {
  font-size: clamp(1.6rem, 1.2rem + 1.6vw, 2.2rem);
  font-weight: 800;
  text-align: center;
  color: var(--text-color);
  letter-spacing: -0.01em;
}

/* Header */
.site-header {
  position: sticky;
  top: 0;
  backdrop-filter: saturate(180%) blur(10px);
  background: color-mix(in oklab, var(--app-bg) 86%, transparent);
  border-bottom: 1px solid
    color-mix(in oklab, var(--text-color) 8%, transparent);
  z-index: 10;
}
.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.brand-logo {
  height: 26px;
  width: auto;
  display: block;
}
.brand-logo.small {
  height: 22px;
  width: auto;
  margin-bottom: 15px;
}
.brand-logo.expanded {
  display: block;
}
.brand-logo.symbol {
  display: none;
}
@media (max-width: 800px) {
  .brand-logo.expanded {
    display: none;
  }
  .brand-logo.symbol {
    display: block;
    height: 26px;
    width: auto;
  }
  .nav-cta {
    gap: 6px;
  }
  .site-header .nav-cta .btn {
    --btn-pad: 8px 10px;
    flex: 0 0 auto;
    width: auto;
    white-space: nowrap;
  }
}
.nav-cta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.site-header .nav-cta .btn {
  font-size: 1rem;
}

/* Buttons */
.btn {
  --btn-bg: var(--hover-bg);
  --btn-br: 12px;
  --btn-pad: 10px 14px;

  background: var(--btn-bg);
  color: var(--btn-fg);
  border: none;
  border-radius: var(--btn-br);
  padding: var(--btn-pad);
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.05s ease, background 0.2s ease, opacity 0.2s ease;
  font-size: 1rem;
}
.btn:hover {
  transform: translateY(-1px);
}
.btn:active {
  transform: translateY(0);
}
.btn-ghost {
  background: transparent;
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
}
.btn-accent {
  --btn-bg: var(--accent);
  --btn-fg: #fff;
}
.btn-lg {
  --btn-pad: 12px 18px;
  border-radius: 14px;
  font-size: 1rem;
}

/* HERO */
.hero {
  position: relative;
  overflow: clip;
  isolation: isolate;
  background: var(--app-bg);
}
.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.gridlines {
  position: absolute;
  inset: -20% -10%;
  background-image: linear-gradient(
      to right,
      color-mix(in oklab, var(--text-color) 6%, transparent) 1px,
      transparent 1px
    ),
    linear-gradient(
      to bottom,
      color-mix(in oklab, var(--text-color) 6%, transparent) 1px,
      transparent 1px
    );
  background-size: 40px 40px;
  mask-image: radial-gradient(900px 600px at 20% 20%, black, transparent 65%);
}

.hero-inner {
  position: relative;
  padding: clamp(28px, 7vw, 90px) 0 clamp(16px, 4vw, 32px);
  display: grid;
  grid-template-columns: 1.12fr 0.88fr;
  gap: 28px;
  align-items: center;
  padding-bottom: 80px;
}
.hero-copy h1 {
  font-size: clamp(2.2rem, 1.6rem + 3.4vw, 3.6rem);
  line-height: 1.06;
  margin: 0 0 12px;
  color: var(--text-color);
  letter-spacing: -0.03em;
}
.underline {
  position: relative;
  white-space: nowrap;
}
.underline::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 10px;
  border-radius: 999px;
  background: color-mix(in oklab, var(--accent) 40%, transparent);
  z-index: -1;
}
.subcopy {
  font-size: 1.1rem;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
  margin: 0 0 18px;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0 14px;
}
.trust {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.chip {
  font-size: 0.85rem;
  background: var(--hover-bg);
  color: var(--text-color);
  padding: 6px 10px;
  border-radius: 999px;
}

/* Hero secondary */
.hero-secondary {
  align-self: start;
  display: flex;
  justify-content: flex-start;
}
.panel {
  background: var(--sidebar-bg);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.kpi-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}
.kpi-item .kpi-label {
  font-size: 0.85rem;
  color: color-mix(in oklab, var(--text-color) 70%, transparent);
}
.kpi-item .kpi-value {
  font-weight: 800;
  font-size: 1.3rem;
}
.kpi-progress .track {
  height: 10px;
  background: var(--hover-bg);
  border-radius: 999px;
  overflow: hidden;
}
.kpi-progress .fill {
  height: 100%;
  background: var(--accent);
  border-radius: 999px;
}
.kpi-progress .kpi-meta {
  margin-top: 6px;
  font-size: 0.85rem;
  color: color-mix(in oklab, var(--text-color) 68%, transparent);
}

/* Principles */
.principles {
  padding: clamp(28px, 6vw, 64px) 0;
  background: color-mix(in oklab, var(--hover-bg) 30%, transparent);
  border-top: 1px solid color-mix(in oklab, var(--text-color) 8%, transparent);
  border-bottom: 1px solid
    color-mix(in oklab, var(--text-color) 8%, transparent);
}
.principles .grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}
.card {
  background: var(--sidebar-bg);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.card .icon {
  font-size: 22px;
  margin-bottom: 8px;
}
.card h3 {
  margin: 0 0 6px;
  font-size: 1.05rem;
  font-weight: 800;
}
.card p {
  margin: 0;
  color: color-mix(in oklab, var(--text-color) 82%, transparent);
  line-height: 1.5;
}

/* Live Preview */
.preview {
  padding: clamp(28px, 6vw, 64px) 0;
}
.preview-head {
  text-align: center;
  max-width: 960px;
  margin: 0 auto 16px;
  margin-bottom: 30px;
}
.preview-head .lead {
  margin: 0;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
}
.preview-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 14px;
  align-items: start;
  margin-top: 10px;
}
.preview .panel {
  background: var(--sidebar-bg);
  border-radius: 14px;
  padding: clamp(12px, 2.2vw, 16px);
}
.preview .deck.panel {
  background: #fff;
}
.preview :deep(.sentence-blank) {
  padding-top: 2rem;
}
.notes h3 {
  margin: 0 0 6px;
}
.notes ul {
  margin: 0 0 12px;
  padding-left: 18px;
  color: color-mix(in oklab, var(--text-color) 82%, transparent);
  line-height: 1.5;
}
.mini-cta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Pricing CTA */
.pricing {
  padding: clamp(28px, 6vw, 64px) 0;
  background: color-mix(in oklab, var(--hover-bg) 24%, transparent);
  border-top: 1px solid color-mix(in oklab, var(--text-color) 8%, transparent);
}
.pricing-card {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 14px;
  align-items: center;
  background: var(--sidebar-bg);
  border-radius: 16px;
  padding: clamp(18px, 3vw, 28px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.pricing-copy h2 {
  margin: 0 0 6px;
}
.pricing-copy p {
  margin: 0;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
}
.plan .price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}
.price .amount {
  font-size: clamp(1.8rem, 1.2rem + 2vw, 2.2rem);
  font-weight: 900;
  letter-spacing: -0.02em;
}
.price .term {
  color: color-mix(in oklab, var(--text-color) 70%, transparent);
}
.plan .features {
  margin: 0 0 10px;
  padding-left: 18px;
  color: color-mix(in oklab, var(--text-color) 82%, transparent);
}
.plan-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Footer */
.site-footer {
  padding: 20px 0 40px;
}
.footer-inner {
  display: grid;
  place-items: center;
  gap: 6px;
}
.brand-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  opacity: 0.9;
}
.fine {
  margin: 0;
  font-size: 0.9rem;
  color: color-mix(in oklab, var(--text-color) 70%, transparent);
}

/* Responsive */
@media (max-width: 980px) {
  .hero-inner {
    grid-template-columns: 1fr;
    padding-bottom: 40px;
  }
  .hero-preview {
    order: 2;
  }
  .preview-grid,
  .pricing-card,
  .principles .grid {
    grid-template-columns: 1fr;
  }
  .preview-grid {
    gap: 2px;
  }
  .pricing-card {
    text-align: center;
  }
  .preview-head {
    margin: 0 auto 4px;
  }
  .preview .panel {
    padding: 8px;
  }
  .preview .notes.panel {
    padding: 15px;
  }
  .notes h3 {
    margin: 0 0 4px;
  }
  .notes ul {
    margin: 0 0 4px;
  }
  .notes .mini-cta {
    margin-top: 4px;
  }

  .preview :deep(.flashcard-card) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    max-width: none;
  }
  .plan .features,
  .plan .features li {
    text-align: left;
  }
  .pricing-copy p {
    display: none;
  }
  .principles .section-title {
    margin-bottom: 0px;
  }
  .plan-actions {
    justify-content: center;
  }
}
</style>
